import React, { useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { agentsOf, callCenterApi, customerByPhone, isValidPhone, useCallCenter } from '../callCenterStore';
import { meName } from '../softphone';

interface Props {
  /** Số điện thoại có sẵn (từ số máy lạ); null = đóng */
  phone: string | null;
  onClose: () => void;
  onCreated?: (id: number) => void;
}

const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).map(v => ({ value: v, label: v }));

export default function CreateCustomerModal({ phone, onClose, onCreated }: Props) {
  const s = useCallCenter();
  const [form] = Form.useForm();
  const services = useMemo(() => uniq(s.customers.map(c => c.service)), [s.customers]);
  const branches = useMemo(() => uniq(s.customers.map(c => c.branch)), [s.customers]);
  const agents = useMemo(() => agentsOf(s).map(a => ({ value: a, label: a })), [s]);

  useEffect(() => {
    if (phone !== null) form.setFieldsValue({ phone, name: '', address: '', service: undefined, branch: undefined, agent: meName() });
  }, [phone, form]);

  const submit = async () => {
    const v = await form.validateFields();
    const id = callCenterApi.createCustomer(v);
    message.success(`Đã tạo khách hàng ${v.name}`);
    onCreated?.(id);
    onClose();
  };

  return (
    <Modal open={phone !== null} title="Tạo khách hàng" onCancel={onClose} onOk={submit} okText="Tạo khách hàng" cancelText="Huỷ" destroyOnClose>
      <Form form={form} layout="vertical" requiredMark="optional" className="pt-2">
        <Form.Item name="phone" label="Số điện thoại" rules={[
          { required: true, message: 'Nhập số điện thoại' },
          { validator: (_, v) => (!v || isValidPhone(v) ? Promise.resolve() : Promise.reject(new Error('Số điện thoại không hợp lệ'))) },
          { validator: (_, v) => (!v || !customerByPhone(s, v) ? Promise.resolve() : Promise.reject(new Error(`Số này đã thuộc khách ${customerByPhone(s, v)?.name}`))) },
        ]}>
          <Input disabled={!!phone} />
        </Form.Item>
        <Form.Item name="name" label="Tên khách hàng" rules={[{ required: true, whitespace: true, message: 'Nhập tên khách hàng' }]}>
          <Input autoFocus placeholder="VD: Nguyễn Văn A" />
        </Form.Item>
        <Form.Item name="address" label="Địa chỉ">
          <Input placeholder="Tỉnh / thành phố" />
        </Form.Item>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Form.Item name="service" label="Dịch vụ quan tâm">
            <Select allowClear showSearch options={services} placeholder="Chọn dịch vụ" />
          </Form.Item>
          <Form.Item name="branch" label="Chi nhánh">
            <Select allowClear options={branches} placeholder="Chọn chi nhánh" />
          </Form.Item>
        </div>
        <Form.Item name="agent" label="Nhân viên phụ trách">
          <Select showSearch options={[{ value: meName(), label: `${meName()} (tôi)` }, ...agents.filter(a => a.value !== meName())]} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
